// src/hooks/usePermisosModulo.js
import { useState, useEffect } from 'react';
import { permisosUsuarioJWT } from '../api/endpoints';

// Cache compartido: una sola llamada HTTP para todos los tabs
let _cache = null;
let _cacheTs = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// Área global: permisos aquí aplican a TODOS los módulos
const AREA_GLOBAL = 'SECRETARÍA GENERAL Y DAP';

function fetchPermisos() {
    const now = Date.now();
    if (!_cache || (now - _cacheTs) > CACHE_TTL) {
        _cacheTs = now;
        _cache = permisosUsuarioJWT().catch(e => {
            _cache = null; // permitir reintentar en caso de error
            throw e;
        });
    }
    return _cache;
}

/**
 * Hook para obtener permisos filtrados por módulo del expediente.
 *
 * Combina permisos del módulo específico + "SECRETARÍA GENERAL Y DAP" (área global).
 * Si el usuario tiene un permiso en el área global, lo tiene en todos los módulos.
 *
 * @param {string} modulo - Nombre del módulo (ej: "EJECUCIONES TRIBUTARIAS Y OTROS...")
 * @returns {{ has, permDe, ready, rawPerms, allPerms }}
 *   - has(funcion, accion): boolean (busca en módulo + área global)
 *   - permDe(funcion, fallback?): { select, insert, update, delete }
 *   - ready: boolean (true cuando los permisos terminaron de cargar)
 *   - rawPerms: objeto crudo de funciones del módulo (para casos especiales)
 *   - allPerms: todos los permisos de todos los módulos (para checks cross-módulo)
 */
export default function usePermisosModulo(modulo) {
    const [modPerms, setModPerms] = useState({});
    const [globalPerms, setGlobalPerms] = useState({});
    const [allPerms, setAllPerms] = useState({});
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const data = await fetchPermisos();
                if (!mounted) return;
                const all = data?.permisos || {};
                setAllPerms(all);

                // Normaliza para comparar nombres de módulo: uppercase + trim +
                // SIN acentos. Antes el match era exacto y fallaba por una tilde
                // (ej. el expediente trae "CRÉDITOS" y el permiso "CREDITOS"),
                // dejando modPerms vacío y habilitando solo vía área global.
                const norm = (s) => (s || '').toUpperCase().trim()
                    .normalize('NFD').replace(/[̀-ͯ]/g, '');

                // Permisos del módulo específico
                const moduloNorm = norm(modulo);
                const moduloKey = Object.keys(all).find(m => norm(m) === moduloNorm) || '';
                setModPerms(moduloKey ? all[moduloKey] : {});

                // Permisos del área global (aplican a todos los módulos)
                const globalKey = Object.keys(all).find(m => norm(m) === norm(AREA_GLOBAL)) || '';
                setGlobalPerms(globalKey ? all[globalKey] : {});
            } catch {
                if (mounted) { setModPerms({}); setGlobalPerms({}); setAllPerms({}); }
            } finally {
                if (mounted) setReady(true);
            }
        })();
        return () => { mounted = false; };
    }, [modulo]);

    /** Verificar un permiso puntual (módulo específico OR área global) */
    const has = (funcion, accion) => {
        const A = (accion || '').toUpperCase();
        // Primero buscar en el módulo específico
        const f = modPerms[funcion];
        if (f && f[A] === true) return true;
        // Fallback: área global
        const g = globalPerms[funcion];
        return !!(g && g[A] === true);
    };

    /** CRUD completo para una función, con fallback opcional a función genérica */
    const permDe = (funcion, fallback = null) => ({
        select: has(funcion, 'SELECT') || (fallback ? has(fallback, 'SELECT') : false),
        insert: has(funcion, 'INSERT') || (fallback ? has(fallback, 'INSERT') : false),
        update: has(funcion, 'UPDATE') || (fallback ? has(fallback, 'UPDATE') : false),
        delete: has(funcion, 'DELETE') || (fallback ? has(fallback, 'DELETE') : false),
    });

    return { has, permDe, ready, rawPerms: modPerms, allPerms };
}
